import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { CheckCircle2, Loader2, Save, Utensils, X } from "lucide-react";
import type {
  StudentDietExecutionItem,
  StudentDietItem,
  StudentDietItemStatus,
  UpdateDietExecutionItemPayload,
} from "@/lib/types";
import { cn } from "@/lib/cn";

/**
 * Modal de registro de um alimento. O aluno descreve o que comeu, marca como
 * foi (como prescrito / modificado / pulado) e deixa notas. "Salvar e
 * continuar" persiste sem concluir; "Marcar refeição" conclui o item.
 */
interface DietItemModalProps {
  open: boolean;
  onClose: () => void;
  /** Item prescrito (vindo do plano). */
  dietItem: StudentDietItem;
  /** Registro existente da execução (se já marcou antes). */
  existing: StudentDietExecutionItem | null;
  onSave: (
    dietItemId: string,
    payload: UpdateDietExecutionItemPayload,
  ) => Promise<{ ok: boolean; error?: string }>;
}

const STATUS_OPTIONS: Array<{
  value: StudentDietItemStatus;
  label: string;
  description: string;
}> = [
  { value: "as_prescribed", label: "Como prescrito", description: "Segui à risca" },
  { value: "modified", label: "Modifiquei", description: "Ajustei algo" },
  { value: "skipped", label: "Pulei", description: "Não comi" },
];

export function DietItemModal({ open, onClose, dietItem, existing, onSave }: DietItemModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const [ateWhat, setAteWhat] = useState<string>(() => existing?.ate_what ?? "");
  const [status, setStatus] = useState<StudentDietItemStatus | null>(() => existing?.status ?? null);
  const [notes, setNotes] = useState<string>(() => existing?.notes ?? "");

  const [submitting, setSubmitting] = useState<"complete" | "save" | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Lock body scroll + Escape pra fechar.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, submitting, onClose]);

  // Auto-foca no primeiro campo ao abrir.
  useEffect(() => {
    if (!open || !dialogRef.current) return;
    const first = dialogRef.current.querySelector<HTMLElement>("textarea, button[data-autofocus]");
    first?.focus();
  }, [open]);

  if (!open) return null;

  function buildPayload(completed: boolean): UpdateDietExecutionItemPayload {
    return {
      ate_what: ateWhat.trim() === "" ? null : ateWhat.trim(),
      status,
      notes: notes.trim() === "" ? null : notes.trim(),
      completed,
    };
  }

  async function submit(completed: boolean) {
    if (submitting) return;
    if (completed && ateWhat.trim().length === 0 && status == null) {
      setError("Conta o que você comeu ou marque o status pra concluir a refeição.");
      return;
    }
    setSubmitting(completed ? "complete" : "save");
    setError(null);
    const res = await onSave(dietItem.id, buildPayload(completed));
    if (res.ok) {
      onClose();
    } else {
      setError(res.error ?? "Não consegui salvar agora.");
      setSubmitting(null);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="diet-modal-title"
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="animate-fade-in relative max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-border-subtle bg-surface/95 backdrop-blur-xl sm:rounded-2xl"
      >
        {/* HEADER — ícone + nome + detalhe prescrito */}
        <header className="sticky top-0 z-10 flex items-start gap-3 border-b border-border-subtle bg-surface/95 px-4 py-3 backdrop-blur-md sm:px-5 sm:py-4">
          <div
            aria-hidden
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-border-subtle bg-elevated"
          >
            <Utensils size={18} className="text-accent-soft" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="diet-modal-title" className="font-display text-base text-ink sm:text-lg">
              {dietItem.line}
            </h2>
            {dietItem.detail ? (
              <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-muted">
                {dietItem.detail}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => !submitting && onClose()}
            disabled={!!submitting}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-faint transition-colors hover:bg-surface-hover hover:text-ink disabled:opacity-50"
            aria-label="Fechar"
          >
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="space-y-4 px-4 py-4 sm:px-5 sm:py-5">
          {/* O que você comeu */}
          <div>
            <p className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted">
              O que você comeu
            </p>
            <p className="mb-1.5 text-[11px] text-faint">Descreva com suas palavras</p>
            <textarea
              value={ateWhat}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setAteWhat(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="ex: 200g de frango grelhado + arroz + salada"
              className="w-full resize-none rounded-lg border border-border-subtle bg-canvas/70 px-3 py-2 text-sm text-ink placeholder:text-faint focus:border-accent-soft focus:outline-none focus:ring-1 focus:ring-accent-soft"
            />
          </div>

          {/* Status: 3 opções */}
          <fieldset>
            <legend className="mb-2 block text-xs font-medium uppercase tracking-wider text-muted">
              Como foi?
            </legend>
            <div className="grid grid-cols-3 gap-2">
              {STATUS_OPTIONS.map((opt) => {
                const selected = status === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setStatus(selected ? null : opt.value)}
                    className={cn(
                      "flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-center transition-all duration-150",
                      selected
                        ? "border-accent-soft bg-accent/15 text-ink shadow-sm"
                        : "border-border-subtle bg-canvas/40 text-muted hover:border-border-strong hover:bg-canvas/60 hover:text-ink",
                    )}
                    aria-pressed={selected}
                    aria-label={`${opt.label}: ${opt.description}`}
                  >
                    <span className={cn("text-sm font-semibold", selected ? "text-ink" : "")}>
                      {opt.label}
                    </span>
                    <span className="text-[10px] text-faint">{opt.description}</span>
                  </button>
                );
              })}
            </div>
          </fieldset>

          {/* Notas */}
          <div>
            <p className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted">
              Notas{" "}
              <span className="ml-1.5 text-[10px] font-normal lowercase tracking-normal text-faint">
                opcional
              </span>
            </p>
            <textarea
              value={notes}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Algo importante pro seu treinador?"
              className="w-full resize-none rounded-lg border border-border-subtle bg-canvas/70 px-3 py-2 text-sm text-ink placeholder:text-faint focus:border-accent-soft focus:outline-none focus:ring-1 focus:ring-accent-soft"
            />
          </div>

          {error ? (
            <div
              role="alert"
              className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
            >
              {error}
            </div>
          ) : null}
        </div>

        <footer className="sticky bottom-0 z-10 flex flex-col-reverse gap-2 border-t border-border-subtle bg-surface/95 px-4 py-3 backdrop-blur-md sm:flex-row sm:items-center sm:justify-end sm:gap-2 sm:px-5 sm:py-4">
          <button
            type="button"
            onClick={() => submit(false)}
            disabled={!!submitting}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-border-subtle bg-elevated px-3 py-2.5 text-sm font-medium text-ink transition-colors hover:border-border-strong hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting === "save" ? (
              <Loader2 size={14} className="animate-spin" aria-hidden />
            ) : (
              <Save size={14} aria-hidden />
            )}
            Salvar e continuar
          </button>
          <button
            type="button"
            data-autofocus
            onClick={() => submit(true)}
            disabled={!!submitting}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-accent px-4 py-2.5 text-sm font-medium text-ink shadow-sm transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting === "complete" ? (
              <Loader2 size={14} className="animate-spin" aria-hidden />
            ) : (
              <CheckCircle2 size={14} aria-hidden />
            )}
            Marcar refeição
          </button>
        </footer>
      </div>
    </div>
  );
}
